import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CREDIT_PACKAGES } from '@boletra/shared';
import { CashIcon, ShieldCheckIcon } from './FeatureIcons.js';
import { usePublicCreditPackages } from '../../lib/orgs.js';

interface Props {
  lang: string;
}

const HIGHLIGHTED_PACKAGE_ID: (typeof CREDIT_PACKAGES)[number]['id'] = 'scale';

export function PricingSection({ lang }: Props) {
  const { t, i18n } = useTranslation('landing');
  const packagesQuery = usePublicCreditPackages();
  const localized = packagesQuery.data;
  const displayCurrency = localized?.displayCurrency ?? 'USD';
  const isEstimate = localized?.isEstimate ?? false;

  // Mientras la respuesta localizada no llegue, mostramos los precios USD
  // canónicos de @boletra/shared para no dejar la grid vacía. Cuando llega,
  // sustituimos sin parpadeo gracias al staleTime de 1h.
  const visiblePackages = localized
    ? localized.packages
    : CREDIT_PACKAGES.map((p) => ({
        id: p.id,
        credits: p.credits,
        totalCents: p.totalUsdCents,
        perUnitCents: p.perUnitCents,
        totalUsdCents: p.totalUsdCents,
        perUnitUsdCents: p.perUnitCents,
      }));

  const fmtLocal = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: displayCurrency,
    minimumFractionDigits: 2,
  });
  const fmtLocalNoCents = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: displayCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const fmtNum = new Intl.NumberFormat(i18n.language);
  const included = [
    t('pricing.included.qr'),
    t('pricing.included.scanner'),
    t('pricing.included.payouts'),
    t('pricing.included.analytics'),
    t('pricing.included.multilang'),
    t('pricing.included.support'),
  ];

  return (
    <section id="pricing" className="border-t border-neutral-900 py-14 sm:py-20 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <p className="text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-2">
            {t('pricing.eyebrow')}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            {t('pricing.title')}
          </h2>
          <p className="text-neutral-400 mt-4 text-base sm:text-lg">{t('pricing.subtitle')}</p>
        </div>

        {/* Free tier hero */}
        <div className="rounded-3xl border border-[var(--color-accent)]/30 bg-gradient-to-br from-[var(--color-accent)]/10 via-neutral-950 to-neutral-950 p-6 sm:p-10 mb-10 sm:mb-14">
          <div className="grid gap-6 lg:gap-10 lg:grid-cols-[1.4fr_1fr] items-center">
            <div>
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[var(--color-accent)] mb-3 px-3 py-1 rounded-full border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10">
                {t('pricing.free_badge')}
              </p>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                {t('pricing.free_title')}
              </h3>
              <p className="text-neutral-400 mt-3 max-w-xl">{t('pricing.free_subtitle')}</p>
              <Link to={`/${lang}/auth/signup`} className="btn-primary mt-6 inline-flex">
                {t('pricing.free_cta')}
              </Link>
            </div>
            <ul className="grid gap-3 text-sm">
              <PerkRow icon={<CashIcon className="w-5 h-5 text-[var(--color-accent)]" />}>
                {t('pricing.perks.no_card')}
              </PerkRow>
              <PerkRow icon={<ShieldCheckIcon className="w-5 h-5 text-[var(--color-accent)]" />}>
                {t('pricing.perks.included')}
              </PerkRow>
              <PerkRow icon={<Check />}>{t('pricing.perks.no_contracts')}</PerkRow>
            </ul>
          </div>
        </div>

        {/* Credit packs */}
        <div className="text-center max-w-xl mx-auto mb-8 sm:mb-10">
          <h3 className="text-xl sm:text-2xl font-bold">{t('pricing.packs_title')}</h3>
          <p className="text-neutral-400 text-sm mt-2">{t('pricing.packs_subtitle')}</p>
        </div>

        {isEstimate && (
          <p className="text-xs text-neutral-500 text-center mb-3">
            {t('pricing.estimated_price_label', { currency: displayCurrency })}
          </p>
        )}

        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4"
          role="list"
          aria-label={t('pricing.packs_aria')}
        >
          {visiblePackages.map((pkg) => {
            const featured = pkg.id === HIGHLIGHTED_PACKAGE_ID;
            const total = pkg.totalCents / 100;
            return (
              <div
                key={pkg.id}
                role="listitem"
                className={[
                  'relative rounded-2xl p-4 sm:p-5 flex flex-col text-left transition',
                  featured
                    ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] border-2 border-[var(--color-accent)] shadow-lg shadow-[var(--color-accent)]/10 scale-[1.02] sm:col-span-1 col-span-2 sm:row-span-1 row-span-1'
                    : 'border border-neutral-800 bg-neutral-900/40 hover:border-neutral-700',
                ].join(' ')}
              >
                {featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full bg-neutral-950 text-[var(--color-accent)] border border-[var(--color-accent)]">
                    {t('pricing.most_popular')}
                  </span>
                )}
                <p
                  className={[
                    'text-[10px] uppercase tracking-widest font-bold mb-3',
                    featured ? 'text-[var(--color-accent-foreground)]/60' : 'text-neutral-500',
                  ].join(' ')}
                >
                  {pkg.id}
                </p>
                <p className="text-3xl sm:text-4xl font-bold leading-none">
                  {fmtNum.format(pkg.credits)}
                </p>
                <p
                  className={[
                    'text-xs mt-1 mb-4',
                    featured ? 'text-[var(--color-accent-foreground)]/70' : 'text-neutral-400',
                  ].join(' ')}
                >
                  {t('pricing.credits_label')}
                </p>
                <div className="mt-auto">
                  <p className="text-2xl font-bold leading-none">
                    {isEstimate ? '≈ ' : ''}
                    {fmtLocal.format(pkg.perUnitCents / 100)}
                  </p>
                  <p
                    className={[
                      'text-[11px] mt-1',
                      featured ? 'text-[var(--color-accent-foreground)]/70' : 'text-neutral-500',
                    ].join(' ')}
                  >
                    {t('pricing.per_ticket_label')}
                  </p>
                  <p
                    className={[
                      'text-xs mt-3 pt-3 border-t',
                      featured
                        ? 'border-[var(--color-accent-foreground)]/20 text-[var(--color-accent-foreground)]/80'
                        : 'border-neutral-800 text-neutral-400',
                    ].join(' ')}
                  >
                    {t('pricing.total_label')}{' '}
                    <span className="font-semibold">
                      {isEstimate ? '≈ ' : ''}
                      {fmtLocalNoCents.format(total)}
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Included in every plan */}
        <div className="mt-12 sm:mt-16 rounded-2xl border border-neutral-900 bg-neutral-950/40 p-6 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="md:max-w-md">
              <h4 className="text-lg sm:text-xl font-bold mb-2">{t('pricing.included_title')}</h4>
              <p className="text-neutral-400 text-sm">{t('pricing.included_subtitle')}</p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm flex-1">
              {included.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-neutral-300">
                  <Check />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-xs text-neutral-500 mt-6 text-center">{t('pricing.footer_note')}</p>
      </div>
    </section>
  );
}

function PerkRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-neutral-900 bg-neutral-950/60 px-3 py-3">
      <span className="flex-shrink-0 mt-0.5">{icon}</span>
      <span className="text-neutral-200">{children}</span>
    </li>
  );
}

function Check() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--color-accent)] flex-shrink-0 mt-1"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
